import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  activatePromotionCode,
  createPromotionCode,
  deactivatePromotionCode,
  deletePromotionCode,
  getPromotionCodeAnalytics,
  getPromotionCodeAnalyticsList,
  getPromotionCodeById,
  getPromotionCodeDetail,
  getPromotionCodes,
  updatePromotionCode,
} from "../services/promotionCodeService";
import {
  CreatePromotionCodeRequest,
  PromotionCode,
  PromotionCodeAnalytics,
  PromotionCodeDetail,
  PromotionCodeList,
  UpdatePromotionCodeRequest,
} from "../types/admin";
import { ApiResponse } from "../types/common";

export const promotionCodeKeys = {
  all: ["promotion-codes"] as const,
  list: () => [...promotionCodeKeys.all, "list"] as const,
  detail: (id: string) => [...promotionCodeKeys.all, "detail", id] as const,
  analytics: (id: string) => [...promotionCodeKeys.all, "analytics", id] as const,
  analyticsList: () => [...promotionCodeKeys.all, "analytics-list"] as const,
};

export const usePromotionCodes = () => {
  return useQuery<ApiResponse<PromotionCodeList[]>>({
    queryKey: promotionCodeKeys.list(),
    queryFn: () => getPromotionCodes(),
    staleTime: 30 * 1000,
  });
};

export const usePromotionCode = (id: string) => {
  return useQuery<ApiResponse<PromotionCode>>({
    queryKey: promotionCodeKeys.detail(id),
    queryFn: () => getPromotionCodeById(id),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
};

export const usePromotionCodeDetail = (id: string) => {
  return useQuery<ApiResponse<PromotionCodeDetail>>({
    queryKey: [...promotionCodeKeys.detail(id), "full"],
    queryFn: () => getPromotionCodeDetail(id),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
};

export const usePromotionCodeAnalytics = (codeId: string) => {
  return useQuery<ApiResponse<PromotionCodeAnalytics>>({
    queryKey: promotionCodeKeys.analytics(codeId),
    queryFn: () => getPromotionCodeAnalytics(codeId),
    enabled: !!codeId,
    staleTime: 60 * 1000,
  });
};

export const usePromotionCodeAnalyticsList = () => {
  return useQuery<ApiResponse<PromotionCodeAnalytics[]>>({
    queryKey: promotionCodeKeys.analyticsList(),
    queryFn: () => getPromotionCodeAnalyticsList(),
    staleTime: 60 * 1000,
  });
};

export const usePromotionCodeMutations = () => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: promotionCodeKeys.list() });
    queryClient.invalidateQueries({
      queryKey: promotionCodeKeys.analyticsList(),
    });
  };

  const create = useMutation({
    mutationFn: (data: CreatePromotionCodeRequest) =>
      createPromotionCode(data),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdatePromotionCodeRequest;
    }) => updatePromotionCode(id, data),
    onSuccess: (_, variables) => {
      invalidate();
      queryClient.invalidateQueries({
        queryKey: promotionCodeKeys.detail(variables.id),
      });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deletePromotionCode(id),
    onSuccess: invalidate,
  });

  const activate = useMutation({
    mutationFn: (id: string) => activatePromotionCode(id),
    onSuccess: (_, id) => {
      invalidate();
      queryClient.invalidateQueries({
        queryKey: promotionCodeKeys.detail(id),
      });
    },
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => deactivatePromotionCode(id),
    onSuccess: (_, id) => {
      invalidate();
      queryClient.invalidateQueries({
        queryKey: promotionCodeKeys.detail(id),
      });
    },
  });

  return { create, update, remove, activate, deactivate };
};

